import { useNavigate } from "react-router-dom";
import { getMenuItemsByUserType } from "@/constants/menu";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { S3Image } from "@/utils/s3-helper";
import AvatarIcon from "@styles/all-images/icons/dashboard-icons/avatar.svg";
import PhoneIcon from "@styles/all-images/icons/dashboard-icons/phone-icon.svg";
import EmailIcon from "@styles/all-images/icons/dashboard-icons/email-icon.svg";
import RoleIcon from "@styles/all-images/icons/dashboard-icons/role-icon.svg";
import LinkIcon from "@styles/all-images/icons/dashboard-icons/link-icon.svg";
import { Skeleton, styled, TableCell } from "@mui/material";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import { BasePermissions } from "@/constants/permission-modules/base-permissions";
const StyledTableCell = styled(TableCell)(() => ({
  padding: "16px 24px",
  fontSize: "14px",
  "&.MuiTableCell-head": {
    backgroundColor: "#fff",
    fontWeight: 500,
    color: "#535862",
  },
}));

const Dashboard = () => {
  const navigate = useNavigate();
  const { isPermitted } = usePermissionState();
  const { user: userData } = useSelector((state: RootState) => state.auth);
  const userType = useSelector((state: any) => state.auth.user?.user_type?.id);

  let menuItems = getMenuItemsByUserType(userData?.user_type?.id);

  menuItems = menuItems.filter((item) => {
    if (item.permissions) {
      return item.permissions.some((permission) =>
        isPermitted(permission as BasePermissions),
      );
    }
    return true;
  });

  const getProfilePicture = () => {
    const img = userData?.profile_picture?.replace(
      "https%3A/9spl-sentinel.s3.us-east-1.amazonaws.com/",
      "",
    );
    return (
      <S3Image
        src={img}
        fallback={AvatarIcon}
        alt="Avatar"
        className="tw-w-6 tw-h-6"
        cacheKey={userData?.profile_picture}
      />
    );
  };

  return (
    <div className="tw-pb-10">
      <div className="tw-container-fluid tw-mx-auto tw-mt-[110px] tw-px-8 dashboard">
        <div className="tw-flex tw-flex-wrap tw-justify-between">
          <div className="sm:tw-w-4/5 tw-w-full tw-px-28">
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-6 md:tw-grid-cols-3 sm:tw-grid-cols-2 tw-gap-5">
              {menuItems.map((item, index) => (
                <div key={index}>
                  <button
                    onClick={() => {
                      if (!item.disabled) {
                        navigate(item.link);
                      }
                    }}
                    className={`tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-[#E9EAEB] dashboard-box-shadow tw-h-[150px] md:tw-h-[110px] tw-w-[150px] md:tw-w-[110px] tw-mb-4 ${
                      item.disabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""
                    }`}
                    disabled={item.disabled}
                  >
                    <div className="tw-flex tw-items-center tw-justify-center">
                      <img
                        src={item.icon}
                        alt={item.title}
                        className={`tw-w-[53px] tw-h-[53px] tw-object-contain ${item.disabled ? "tw-w-[53px] tw-h-[65px] tw-cursor-not-allowed" : ""}`}
                      />
                    </div>
                  </button>
                  <div className="tw-text-center tw-w-[150px] md:tw-w-[110px]">
                    <button
                      className="font-size-base tw-text-center tw-font-semibold font-primary font-primary-color tw-leading-5 tw-bg-transparent tw-border-0"
                      onClick={() => {
                        if (!item.disabled) {
                          navigate(item.link);
                        }
                      }}
                    >
                      {item.title}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="sm:tw-w-1/5 tw-w-full tw-flex tw-justify-end">
            <div className="tw-fixed tw-w-[290px] tw-h-[620px] tw-z-10">
              <div className="tw-flex tw-flex-col tw-gap-6">
                <div className="tw-flex tw-justify-end">
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="tw-gap-2 tw-text-white tw-rounded-md primary-custom-button"
                  >
                    Go to Dashboard
                  </button>
                </div>
                <div className="tw-p-6 tw-flex tw-flex-col tw-bg-white tw-border tw-rounded-md tw-border-[#E9EAEB]">
                  {/* Profile Section */}
                  {userData ? (
                    <div className="tw-flex tw-flex-col">
                      <div className="tw-flex tw-items-center tw-flex-col tw-gap-3 tw-mb-4">
                        <div className="tw-w-10 tw-h-10 tw-border tw-rounded-[10px] tw-flex tw-items-center tw-justify-center">
                          {getProfilePicture()}
                        </div>
                        <h2 className="tw-text-[16px] tw-font-semibold font-primary font-primary-color tw-leading-5">
                          {`${userData?.first_name || ""} ${userData?.last_name || ""}`}
                        </h2>
                      </div>
                      <div className="tw-flex tw-flex-col tw-gap-2">
                        {userData?.phone && (
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <a
                              href={`tel:${userData.phone}`}
                              className="tw-text-[15px] tw-text-[#717680] tw-flex tw-items-center tw-gap-2"
                            >
                              <img
                                src={PhoneIcon}
                                alt="Phone"
                                className="tw-w-5 tw-h-5"
                              />
                              {userData.phone}
                            </a>
                          </div>
                        )}
                        {userData?.email && (
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <a
                              href={`mailto:${userData.email}`}
                              className="tw-text-[15px] tw-text-[#717680] tw-flex tw-items-center tw-gap-2"
                            >
                              <img
                                src={EmailIcon}
                                alt="Email"
                                className="tw-w-5 tw-h-5"
                              />
                              {userData.email}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <StyledTableCell>
                      <Skeleton animation="wave" height={24} width={24} />
                    </StyledTableCell>
                  )}

                  {/* Company Details */}
                  {userData?.companies?.[0] && (
                    <div className="tw-flex tw-flex-col tw-gap-3 tw-mt-12">
                      <h2 className="tw-text-[16px] tw-font-semibold tw-border-b tw-pb-2">
                        Company Details
                      </h2>
                      <div className="tw-flex tw-flex-col tw-gap-2">
                        <h3 className="tw-text-[15px] tw-font-medium font-primary font-primary-color tw-leading-5">
                          {userData.companies[0].name}
                        </h3>
                        <div className="tw-flex tw-flex-col tw-gap-2">
                          {userData.companies[0]
                            .administrative_contact_number && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <a
                                href={`tel:${userData.companies[0].administrative_contact_number}`}
                                className="tw-text-[15px] tw-text-[#717680] tw-flex tw-items-center tw-gap-2"
                              >
                                <img
                                  src={PhoneIcon}
                                  alt="Phone"
                                  className="tw-w-5 tw-h-5"
                                />
                                {
                                  userData.companies[0]
                                    .administrative_contact_number
                                }
                              </a>
                            </div>
                          )}
                          {userData.companies[0].administrative_email && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <a
                                href={`mailto:${userData.companies[0].administrative_email}`}
                                className="tw-text-[15px] tw-text-[#717680] tw-flex tw-items-center tw-gap-2"
                              >
                                <img
                                  src={EmailIcon}
                                  alt="Email"
                                  className="tw-w-5 tw-h-5"
                                />
                                {userData.companies[0].administrative_email}
                              </a>
                            </div>
                          )}
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <span className="tw-text-[15px] tw-text-[#717680] tw-flex tw-items-center tw-gap-2">
                              <img
                                src={RoleIcon}
                                alt="User"
                                className="tw-w-5 tw-h-5"
                              />
                              {userData?.default_profile?.name ||
                                "Operational User"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Useful Links */}
                  <div className="tw-flex tw-flex-col tw-gap-3 tw-mt-12">
                    <h2 className="tw-text-[15px] tw-font-semibold tw-border-b tw-pb-2">
                      Useful Link
                    </h2>
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <a
                        href="/staff-management"
                        className="tw-text-[15px] tw-text-[#146C94] tw-flex tw-items-center tw-gap-2"
                      >
                        <img
                          src={LinkIcon}
                          alt="Link"
                          className="tw-w-5 tw-h-5"
                        />
                        Add Team Members
                      </a>
                      {userType === UserTierType.Tier3 && (
                        <a
                          href="/document/to-be-approved"
                          className="tw-text-[15px] tw-text-[#146C94] tw-flex tw-items-center tw-gap-2"
                        >
                          <img
                            src={LinkIcon}
                            alt="Link"
                            className="tw-w-5 tw-h-5"
                          />
                          Documents Approval
                        </a>
                      )}
                      {userType !== UserTierType.Tier3 && (
                        <a
                          href={`/live-chat?screen=user&id=${userData?.admin_details?.admin_id}&company_id=${userData?.admin_details?.company_id}`}
                          className="tw-text-[15px] tw-text-[#146C94] tw-flex tw-items-center tw-gap-2"
                        >
                          <img
                            src={LinkIcon}
                            alt="Link"
                            className="tw-w-5 tw-h-5"
                          />
                          Chat with iBanq
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
    </div>
  );
};

export default Dashboard;
